import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, CreditCard, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Shield, BarChart3, Globe, Search, Download, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../services/api';

// ==================== STATS ====================

function StatsBar({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Firmy', value: stats.total_tenants, sub: `${stats.active_tenants} aktywnych`, color: 'blue' },
        { label: 'Użytkownicy', value: stats.total_users, color: 'emerald' },
        { label: 'Plany', value: stats.by_plan?.length || 0, color: 'amber' },
      ].map(s => (
        <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-800">{s.value}</div>
          <div className="text-sm text-gray-500">{s.label}</div>
          {s.sub && <div className="text-xs text-gray-400 mt-1">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ==================== TENANT FORM ====================

function TenantForm({ initial, plans, onSave, onCancel }) {
  const i = initial || {};
  const [form, setForm] = useState({
    name: i.name || '', slug: i.slug || '', plan: i.plan || 'basic', max_users: i.max_users || 5,
    is_active: i.is_active ?? true,
    // Dane firmy
    company_full_name: i.company_full_name || '', nip: i.nip || '', regon: i.regon || '', krs: i.krs || '',
    // Kontakt
    contact_person: i.contact_person || '', contact_email: i.contact_email || '',
    contact_email2: i.contact_email2 || '', contact_phone: i.contact_phone || '', website: i.website || '',
    // Adres siedziby
    address_street: i.address_street || '', address_city: i.address_city || '',
    address_zip: i.address_zip || '', address_country: i.address_country || 'Polska',
    // Adres wysyłkowy
    shipping_street: i.shipping_street || '', shipping_city: i.shipping_city || '',
    shipping_zip: i.shipping_zip || '', shipping_country: i.shipping_country || 'Polska',
    shipping_contact_person: i.shipping_contact_person || '', shipping_phone: i.shipping_phone || '',
    // Zamówienia
    order_email: i.order_email || '', order_notes: i.order_notes || '',
    // Profil biznesowy
    business_description: i.business_description || '', business_category: i.business_category || '',
    business_tags: i.business_tags || [], is_public: i.is_public ?? true,
    // Inne
    notes: i.notes || '',
    // Admin (nowa firma)
    admin_name: '', admin_email: '', admin_password: '',
  });
  const [error, setError] = useState('');
  const [copyAddress, setCopyAddress] = useState(false);

  const generateSlug = (name) => name.toLowerCase()
    .replace(/[ąàáâãäå]/g, 'a').replace(/[ćçč]/g, 'c').replace(/[ďð]/g, 'd')
    .replace(/[ęèéêë]/g, 'e').replace(/[ìíîï]/g, 'i').replace(/[ł]/g, 'l')
    .replace(/[ńñ]/g, 'n').replace(/[óòôõö]/g, 'o').replace(/[řŕ]/g, 'r')
    .replace(/[śšş]/g, 's').replace(/[ťţ]/g, 't').replace(/[úùûü]/g, 'u')
    .replace(/[ýÿ]/g, 'y').replace(/[źżž]/g, 'z').replace(/[ß]/g, 'ss')
    .replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').substring(0, 100);

  const handleNameChange = (name) => setForm(f => ({ ...f, name, slug: initial ? f.slug : generateSlug(name) }));

  const handlePlanChange = (planKey) => {
    const plan = plans.find(p => p.key === planKey);
    setForm(f => ({ ...f, plan: planKey, max_users: plan?.max_users || f.max_users }));
  };

  const handleCopyAddress = () => {
    setCopyAddress(true);
    setForm(f => ({ ...f,
      shipping_street: f.address_street, shipping_city: f.address_city,
      shipping_zip: f.address_zip, shipping_country: f.address_country
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault(); setError('');
    if (!initial && form.admin_password.length < 6) { setError('Hasło musi mieć min. 6 znaków'); return; }
    onSave(form);
  };

  const ic = "w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500";
  const lc = "block text-xs font-medium text-gray-600 mb-1";
  const sc = "border-t pt-3 mt-1";
  const sh = "text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3";
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <form onSubmit={handleSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

      {/* Podstawowe */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lc}>Nazwa firmy (skrócona) *</label>
          <input type="text" value={form.name} onChange={e => handleNameChange(e.target.value)} className={ic} required autoFocus />
        </div>
        {!initial ? (
          <div><label className={lc}>Slug (URL) *</label><input type="text" value={form.slug} onChange={set('slug')} className={ic + " font-mono"} required /></div>
        ) : (
          <div><label className={lc}>Slug</label><div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 font-mono">{form.slug}</div></div>
        )}
      </div>
      <div><label className={lc}>Pełna nazwa firmy</label><input type="text" value={form.company_full_name} onChange={set('company_full_name')} className={ic} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className={lc}>NIP</label><input type="text" value={form.nip} onChange={set('nip')} className={ic} /></div>
        <div><label className={lc}>REGON</label><input type="text" value={form.regon} onChange={set('regon')} className={ic} /></div>
        <div><label className={lc}>KRS</label><input type="text" value={form.krs} onChange={set('krs')} className={ic} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lc}>Plan</label>
          <select value={form.plan} onChange={e => handlePlanChange(e.target.value)} className={ic}>
            {plans.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
        </div>
        <div><label className={lc}>Maks. użytkowników</label><input type="number" min="1" value={form.max_users} onChange={set('max_users')} className={ic} /></div>
      </div>

      {initial && (
        <div className="flex items-center gap-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="sr-only peer" />
            <div className="w-9 h-5 bg-gray-200 peer-checked:bg-green-500 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
          </label>
          <span className="text-sm">Firma aktywna</span>
        </div>
      )}

      {/* Kontakt */}
      <div className={sc}><h3 className={sh}>Dane kontaktowe</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lc}>Osoba kontaktowa</label><input type="text" value={form.contact_person} onChange={set('contact_person')} className={ic} /></div>
          <div><label className={lc}>Telefon</label><input type="text" value={form.contact_phone} onChange={set('contact_phone')} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><label className={lc}>Email kontaktowy</label><input type="email" value={form.contact_email} onChange={set('contact_email')} className={ic} /></div>
          <div><label className={lc}>Email dodatkowy</label><input type="email" value={form.contact_email2} onChange={set('contact_email2')} className={ic} /></div>
        </div>
        <div className="mt-3"><label className={lc}>Strona WWW</label><input type="text" value={form.website} onChange={set('website')} className={ic} /></div>
      </div>

      {/* Adres siedziby */}
      <div className={sc}><h3 className={sh}>Adres siedziby</h3>
        <div><label className={lc}>Ulica i numer</label><input type="text" value={form.address_street} onChange={set('address_street')} className={ic} /></div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div><label className={lc}>Kod pocztowy</label><input type="text" value={form.address_zip} onChange={set('address_zip')} className={ic} /></div>
          <div><label className={lc}>Miasto</label><input type="text" value={form.address_city} onChange={set('address_city')} className={ic} /></div>
          <div><label className={lc}>Kraj</label><input type="text" value={form.address_country} onChange={set('address_country')} className={ic} /></div>
        </div>
      </div>

      {/* Adres wysyłkowy */}
      <div className={sc}><h3 className={sh}>Adres wysyłkowy</h3>
        <button type="button" onClick={handleCopyAddress} className="text-xs text-blue-600 hover:underline mb-2">Kopiuj z adresu siedziby</button>
        <div><label className={lc}>Ulica i numer</label><input type="text" value={form.shipping_street} onChange={set('shipping_street')} className={ic} /></div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div><label className={lc}>Kod pocztowy</label><input type="text" value={form.shipping_zip} onChange={set('shipping_zip')} className={ic} /></div>
          <div><label className={lc}>Miasto</label><input type="text" value={form.shipping_city} onChange={set('shipping_city')} className={ic} /></div>
          <div><label className={lc}>Kraj</label><input type="text" value={form.shipping_country} onChange={set('shipping_country')} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><label className={lc}>Osoba odbierająca</label><input type="text" value={form.shipping_contact_person} onChange={set('shipping_contact_person')} className={ic} /></div>
          <div><label className={lc}>Telefon do wysyłki</label><input type="text" value={form.shipping_phone} onChange={set('shipping_phone')} className={ic} /></div>
        </div>
      </div>

      {/* Zamówienia */}
      <div className={sc}><h3 className={sh}>Zamówienia</h3>
        <div><label className={lc}>Email do zamówień</label><input type="email" value={form.order_email} onChange={set('order_email')} className={ic} /></div>
        <div className="mt-3"><label className={lc}>Uwagi do zamówień</label>
          <textarea value={form.order_notes} onChange={set('order_notes')} className={ic} rows={2} /></div>
      </div>

      {/* Profil biznesowy */}
      <div className={sc}><h3 className={sh}>Profil biznesowy (katalog firm)</h3>
        <div><label className={lc}>Opis działalności</label>
          <textarea value={form.business_description} onChange={set('business_description')} className={ic} rows={3}
            placeholder="Opis działalności firmy widoczny w katalogu firm..." /></div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><label className={lc}>Kategoria / Branża</label>
            <input type="text" value={form.business_category} onChange={set('business_category')} className={ic}
              placeholder="np. Budownictwo, Reklama, IT" /></div>
          <div><label className={lc}>Tagi (oddzielone przecinkiem)</label>
            <input type="text" value={(form.business_tags || []).join(', ')}
              onChange={e => setForm(f => ({ ...f, business_tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
              className={ic} placeholder="np. stal, blacha, montaż" /></div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))} className="sr-only peer" />
            <div className="w-9 h-5 bg-gray-200 peer-checked:bg-green-500 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
          </label>
          <span className="text-sm">Firma widoczna w katalogu</span>
        </div>
      </div>

      {/* Notatki */}
      <div><label className={lc}>Notatki wewnętrzne</label>
        <textarea value={form.notes} onChange={set('notes')} className={ic} rows={2} /></div>

      {/* Admin */}
      {!initial && (
        <div className={sc}><h3 className={sh}>Administrator firmy</h3>
          <div className="space-y-3">
            <div><label className={lc}>Imię i nazwisko *</label><input type="text" value={form.admin_name} onChange={set('admin_name')} className={ic} required /></div>
            <div><label className={lc}>Email *</label><input type="email" value={form.admin_email} onChange={set('admin_email')} className={ic} required /></div>
            <div><label className={lc}>Hasło (min. 6 znaków) *</label><input type="password" value={form.admin_password} onChange={set('admin_password')} className={ic} required minLength={6} /></div>
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-3 border-t mt-3 sticky bottom-0 bg-white pb-1">
        <button type="submit" className="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
          {initial ? 'Zapisz zmiany' : 'Utwórz firmę'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300">Anuluj</button>
      </div>
    </form>
  );
}

// ==================== TENANT ROW ====================

function TenantRow({ tenant, plans, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState(null);

  const loadUsers = async () => {
    if (users) { setExpanded(!expanded); return; }
    try {
      const res = await api.get(`/superadmin/tenants/${tenant.id}`);
      setUsers(res.data.users);
      setExpanded(true);
    } catch { /* silent */ }
  };

  const plan = plans.find(p => p.key === tenant.plan);

  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-gray-50 group">
        <td className="px-4 py-3">
          <button onClick={loadUsers} className="mr-2 text-gray-400 hover:text-gray-600">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <span className="font-medium text-gray-800">{tenant.name}</span>
          <span className="ml-2 text-xs text-gray-400 font-mono">{tenant.slug}</span>
        </td>
        <td className="px-4 py-3">
          {plan && <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: plan.color + '20', color: plan.color }}>{plan.name}</span>}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">{tenant.users_count} / {tenant.max_users}</td>
        <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded text-xs ${tenant.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {tenant.is_active ? 'Aktywna' : 'Nieaktywna'}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-gray-400">{new Date(tenant.created_at).toLocaleDateString('pl-PL')}</td>
        <td className="px-4 py-3 text-right">
          <button onClick={() => onEdit(tenant)} className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100">
            <Pencil size={14} className="text-gray-500" />
          </button>
        </td>
      </tr>
      {expanded && users && (
        <tr>
          <td colSpan={6} className="px-8 py-3 bg-gray-50">
            <div className="text-xs text-gray-500 mb-2 font-medium">Użytkownicy ({users.length})</div>
            <div className="grid gap-1">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-3 text-xs">
                  <span className={`w-2 h-2 rounded-full ${u.is_active && !u.is_archived ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="font-medium text-gray-700 w-40">{u.name}</span>
                  <span className="text-gray-400 w-48">{u.email}</span>
                  <span className="text-gray-500">{u.role}</span>
                </div>
              ))}
            </div>
            {tenant.contact_email && <div className="mt-2 text-xs text-gray-400">Kontakt: {tenant.contact_email} {tenant.contact_phone}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

// ==================== PLAN FORM ====================

function PlanForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    key: initial?.key || '', name: initial?.name || '', description: initial?.description || '',
    max_users: initial?.max_users || 5, color: initial?.color || '#6B7280',
    modules: initial?.modules || [], is_active: initial?.is_active ?? true,
  });
  const [availableModules, setAvailableModules] = useState([]);

  useEffect(() => {
    api.get('/modules').then(r => setAvailableModules(r.data.modules || [])).catch(() => {});
  }, []);

  const toggleModule = (key) => {
    setForm(f => ({ ...f, modules: f.modules.includes(key) ? f.modules.filter(m => m !== key) : [...f.modules, key] }));
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Klucz *</label>
          <input type="text" value={form.key} onChange={e => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" required disabled={!!initial} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa *</label>
          <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required autoFocus />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max użytkowników</label>
          <input type="number" min="1" value={form.max_users} onChange={e => setForm({ ...form, max_users: parseInt(e.target.value) || 5 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kolor</label>
          <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-full h-10 rounded-lg border border-gray-300 cursor-pointer" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
          <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
      </div>
      {availableModules.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Moduły</label>
          <div className="flex flex-wrap gap-2">
            {availableModules.map(m => (
              <button key={m.key} type="button" onClick={() => toggleModule(m.key)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition ${form.modules.includes(m.key) ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'bg-gray-100 text-gray-500'}`}>
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {initial && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" />
          Plan aktywny
        </label>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">{initial ? 'Zapisz' : 'Utwórz plan'}</button>
      </div>
    </form>
  );
}

// ==================== MAIN PAGE ====================

export default function SuperAdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('tenants');
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);

  const load = useCallback(async () => {
    try {
      const [statsRes, tenantsRes, plansRes] = await Promise.all([
        api.get('/superadmin/stats'),
        api.get('/superadmin/tenants'),
        api.get('/superadmin/plans'),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data.tenants);
      setPlans(plansRes.data.plans);
    } catch { toast.error(t('superAdmin.loadingError')); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleSaveTenant = async (data) => {
    try {
      if (editingTenant) {
        await api.put(`/superadmin/tenants/${editingTenant.id}`, data);
        toast.success(t('superAdmin.tenantUpdated'));
      } else {
        await api.post('/superadmin/tenants', data);
        toast.success(t('superAdmin.tenantCreated'));
      }
      setShowTenantForm(false); setEditingTenant(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleSavePlan = async (data) => {
    try {
      if (editingPlan) {
        await api.put(`/superadmin/plans/${editingPlan.id}`, data);
        toast.success(t('superAdmin.planUpdated'));
      } else {
        await api.post('/superadmin/plans', data);
        toast.success(t('superAdmin.planCreated'));
      }
      setShowPlanForm(false); setEditingPlan(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDeletePlan = async () => {
    try {
      await api.delete(`/superadmin/plans/${deletePlan.id}`);
      toast.success(t('superAdmin.planDeleted'));
      setDeletePlan(null); load();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  if (!user?.is_superadmin) {
    return <div className="p-8 text-center text-gray-400"><Shield size={48} className="mx-auto mb-4 opacity-50" /><p>{t('superAdmin.requiresSuperAdmin')}</p></div>;
  }

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Shield size={28} className="text-blue-600" /> Super Admin
          </h1>
          <p className="text-gray-500 mt-1">{t('superAdmin.subtitle')}</p>
        </div>
      </div>

      <StatsBar stats={stats} />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b">
        {[
          { key: 'tenants', label: t('superAdmin.tenantsTab'), icon: Building2 },
          { key: 'plans', label: t('superAdmin.plansTab'), icon: CreditCard },
          { key: 'translations', label: t('superAdmin.translationsTab') || 'Tłumaczenia', icon: Globe },
          { key: 'variables', label: t('superAdmin.variablesTab') || 'Zmienne', icon: Search },
        ].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === tb.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>

      {/* TENANTS TAB */}
      {tab === 'tenants' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditingTenant(null); setShowTenantForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
              <Plus size={16} /> {t('superAdmin.newTenant')}
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <th className="text-left px-4 py-3 font-medium">{t('superAdmin.tenantsTab')}</th>
                  <th className="text-left px-4 py-3 font-medium">{t('superAdmin.plan')}</th>
                  <th className="text-left px-4 py-3 font-medium">{t('superAdmin.users')}</th>
                  <th className="text-left px-4 py-3 font-medium">{t('common.status')}</th>
                  <th className="text-left px-4 py-3 font-medium">{t('superAdmin.date')}</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <TenantRow key={t.id} tenant={t} plans={plans}
                    onEdit={(t) => { setEditingTenant(t); setShowTenantForm(true); }} />
                ))}
                {tenants.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('superAdmin.noTenants')}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PLANS TAB */}
      {tab === 'plans' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditingPlan(null); setShowPlanForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
              <Plus size={16} /> {t('superAdmin.newPlan')}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {plans.map(p => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 group">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                    <h3 className="font-medium text-gray-800">{p.name}</h3>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                    <button onClick={() => { setEditingPlan(p); setShowPlanForm(true); }} className="p-1 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>
                    <button onClick={() => setDeletePlan(p)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Klucz: <span className="font-mono">{p.key}</span></div>
                  <div>Max użytkowników: {p.max_users}</div>
                  {p.description && <div>{p.description}</div>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(p.modules || []).map(m => (
                      <span key={m} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">{m}</span>
                    ))}
                  </div>
                  {!p.is_active && <div className="text-red-500 font-medium mt-1">Nieaktywny</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={showTenantForm} onClose={() => { setShowTenantForm(false); setEditingTenant(null); }}
        title={editingTenant ? t('superAdmin.editTenant') : t('superAdmin.newTenant')} maxWidth="max-w-3xl">
        <TenantForm initial={editingTenant} plans={plans} onSave={handleSaveTenant}
          onCancel={() => { setShowTenantForm(false); setEditingTenant(null); }} />
      </Modal>

      <Modal isOpen={showPlanForm} onClose={() => { setShowPlanForm(false); setEditingPlan(null); }}
        title={editingPlan ? t('superAdmin.editPlan') : t('superAdmin.newPlan')} maxWidth="max-w-xl">
        <PlanForm initial={editingPlan} onSave={handleSavePlan}
          onCancel={() => { setShowPlanForm(false); setEditingPlan(null); }} />
      </Modal>

      {/* TRANSLATIONS TAB */}
      {tab === 'translations' && <TranslationsManager toast={toast} />}
      {tab === 'variables' && <VariableAliasesManager toast={toast} />}

      <ConfirmDialog isOpen={!!deletePlan} type="danger"
        title={t('superAdmin.deletePlan')} message={t('superAdmin.deletePlanConfirm', { name: deletePlan?.name })}
        onConfirm={handleDeletePlan} onCancel={() => setDeletePlan(null)} />
    </div>
  );
}

function VariableAliasesManager({ toast }) {
  const { t } = useTranslation();
  const [aliases, setAliases] = useState({}); // { canonical: { pl: "alias", en: "alias", de: "alias" } }
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editCell, setEditCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newCanonical, setNewCanonical] = useState('');

  const load = useCallback(async () => {
    try {
      const [ar, lr] = await Promise.all([
        api.get('/pricelists/variable-aliases'),
        api.get('/superadmin/translations') // get languages
      ]);
      setAliases(ar.data.aliases || {});
      setLanguages(lr.data.languages || []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const langCodes = languages.filter(l => l.is_active).map(l => l.code);

  const handleSave = async (canonical, lang, alias) => {
    try {
      await api.put('/superadmin/variable-aliases', { canonical_name: canonical, lang, alias: alias.toLowerCase().replace(/[^a-z0-9_]/g, '') });
      setAliases(prev => ({ ...prev, [canonical]: { ...prev[canonical], [lang]: alias.toLowerCase().replace(/[^a-z0-9_]/g, '') } }));
      setEditCell(null);
    } catch { toast.error(t('common.error')); }
  };

  const handleAdd = async () => {
    const clean = newCanonical.toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!clean) return;
    try {
      for (const lang of langCodes) {
        await api.put('/superadmin/variable-aliases', { canonical_name: clean, lang, alias: clean });
      }
      setNewCanonical('');
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleDelete = async (canonical) => {
    try {
      await api.delete(`/superadmin/variable-aliases/${canonical}`);
      load();
    } catch { toast.error(t('common.error')); }
  };

  const filtered = Object.entries(aliases).filter(([canonical]) =>
    !search || canonical.includes(search.toLowerCase()) ||
    Object.values(aliases[canonical] || {}).some(a => a.includes(search.toLowerCase()))
  );

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">Słownik aliasów zmiennych — zmienne o różnych nazwach w różnych językach są traktowane identycznie w formułach.<br/>
        Np. <code className="bg-gray-100 px-1 rounded">l_rabat</code> (PL) = <code className="bg-gray-100 px-1 rounded">l_discount</code> (EN) = <code className="bg-gray-100 px-1 rounded">l_rabatt</code> (DE)</p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Szukaj zmiennej..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <input type="text" value={newCanonical} onChange={e => setNewCanonical(e.target.value)}
            placeholder="Nowa zmienna (nazwa kanoniczna)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64" />
          <button onClick={handleAdd} disabled={!newCanonical.trim()}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm">+ Dodaj</button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: 'calc(100vh - 350px)' }}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-500 w-48">Nazwa kanoniczna</th>
              {langCodes.map(lang => (
                <th key={lang} className="text-left px-3 py-2 font-medium text-gray-500">
                  {languages.find(l => l.code === lang)?.native_name || lang} ({lang})
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(([canonical, langAliases]) => (
              <tr key={canonical} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-1.5 font-mono text-purple-600 font-medium">{canonical}</td>
                {langCodes.map(lang => {
                  const val = langAliases[lang] || '';
                  const isEditing = editCell?.canonical === canonical && editCell?.lang === lang;
                  const isDifferent = val && val !== canonical;
                  return (
                    <td key={lang} className={`px-3 py-1.5 ${!val ? 'bg-red-50' : isDifferent ? 'bg-blue-50' : ''}`}>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)}
                            className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm font-mono" autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') handleSave(canonical, lang, editValue); if (e.key === 'Escape') setEditCell(null); }} />
                          <button onClick={() => handleSave(canonical, lang, editValue)}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs">OK</button>
                        </div>
                      ) : (
                        <span onClick={() => { setEditCell({ canonical, lang }); setEditValue(val); }}
                          className={`cursor-pointer hover:bg-blue-100 rounded px-1 py-0.5 block font-mono ${!val ? 'text-red-400 italic' : 'text-gray-700'}`}>
                          {val || '— brak —'}
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="px-1">
                  <button onClick={() => handleDelete(canonical)} className="p-1 hover:bg-red-50 rounded">
                    <Trash2 size={12} className="text-red-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TranslationsManager({ toast }) {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [nsFilter, setNsFilter] = useState('');
  const [showAddLang, setShowAddLang] = useState(false);
  const [newLang, setNewLang] = useState({ code: '', name: '', native_name: '' });
  const [editCell, setEditCell] = useState(null); // { ns, key, lang }
  const [editValue, setEditValue] = useState('');

  const load = useCallback(async () => {
    try {
      let r = await api.get('/superadmin/translations');
      // Auto-import if DB is empty
      if ((r.data.translations || []).length === 0) {
        await api.post('/superadmin/translations/import');
        r = await api.get('/superadmin/translations');
      }
      setLanguages(r.data.languages || []);
      setTranslations(r.data.translations || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async () => {
    try {
      const r = await api.post('/superadmin/translations/import');
      toast.success(`Zaimportowano ${r.data.count} tłumaczeń`);
      load();
    } catch { toast.error(t('common.error')); }
  };

  const handleExport = async () => {
    try {
      const r = await api.post('/superadmin/translations/export');
      toast.success(`Wyeksportowano do plików: ${r.data.languages.join(', ')}`);
    } catch { toast.error(t('common.error')); }
  };

  const handleSave = async (namespace, key, lang, value) => {
    try {
      await api.put('/superadmin/translations', { namespace, key, lang, value });
      setTranslations(prev => prev.map(tr =>
        tr.namespace === namespace && tr.key === key ? { ...tr, [lang]: value } : tr
      ));
      setEditCell(null);
      // Invalidate cache so changes are picked up
      api.post('/translations/invalidate').catch(() => {});
    } catch { toast.error(t('common.error')); }
  };

  const handleAddLang = async () => {
    if (!newLang.code || !newLang.name || !newLang.native_name) return;
    try {
      await api.post('/superadmin/translations/language', newLang);
      toast.success(`Język ${newLang.name} dodany`);
      setShowAddLang(false);
      setNewLang({ code: '', name: '', native_name: '' });
      load();
    } catch { toast.error(t('common.error')); }
  };

  const namespaces = [...new Set(translations.map(tr => tr.namespace))].sort();
  const langCodes = languages.filter(l => l.is_active).map(l => l.code);

  const filtered = translations.filter(tr => {
    if (nsFilter && tr.namespace !== nsFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return tr.key.toLowerCase().includes(q) ||
        langCodes.some(l => (tr[l] || '').toLowerCase().includes(q));
    }
    return true;
  });

  // Find missing translations
  const getMissing = (tr) => langCodes.filter(l => !tr[l] || tr[l].trim() === '');

  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Szukaj klucza lub tłumaczenia..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <select value={nsFilter} onChange={e => setNsFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Wszystkie sekcje</option>
          {namespaces.map(ns => <option key={ns} value={ns}>{ns}</option>)}
        </select>
        <button onClick={handleImport} className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
          <Download size={14} /> Import z plików
        </button>
        <button onClick={handleExport} className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
          <Upload size={14} /> Eksport do plików
        </button>
        <button onClick={() => setShowAddLang(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
          <Globe size={14} /> + Język
        </button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-3 text-sm text-gray-500">
        <span>{translations.length} kluczy</span>
        <span>{langCodes.length} języków ({langCodes.join(', ')})</span>
        <span className="text-amber-600">
          {translations.filter(tr => getMissing(tr).length > 0).length} brakujących tłumaczeń
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-500 w-16">Sekcja</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500 w-48">Klucz</th>
              {langCodes.map(lang => (
                <th key={lang} className="text-left px-3 py-2 font-medium text-gray-500">
                  {languages.find(l => l.code === lang)?.native_name || lang}
                  <span className="text-xs text-gray-400 ml-1">({lang})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((tr, idx) => {
              const missing = getMissing(tr);
              return (
                <tr key={`${tr.namespace}.${tr.key}`} className={`border-b border-gray-50 hover:bg-gray-50 ${missing.length > 0 ? 'bg-amber-50' : ''}`}>
                  <td className="px-3 py-1.5 text-xs text-purple-600 font-mono">{tr.namespace}</td>
                  <td className="px-3 py-1.5 text-xs text-gray-700 font-mono">{tr.key}</td>
                  {langCodes.map(lang => {
                    const isEditing = editCell?.ns === tr.namespace && editCell?.key === tr.key && editCell?.lang === lang;
                    const val = tr[lang] || '';
                    const isMissing = !val.trim();
                    return (
                      <td key={lang} className={`px-3 py-1.5 ${isMissing ? 'bg-red-50' : ''}`}>
                        {isEditing ? (
                          <div className="flex gap-1">
                            <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)}
                              className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm" autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') handleSave(tr.namespace, tr.key, lang, editValue); if (e.key === 'Escape') setEditCell(null); }} />
                            <button onClick={() => handleSave(tr.namespace, tr.key, lang, editValue)}
                              className="px-2 py-1 bg-blue-600 text-white rounded text-xs">OK</button>
                          </div>
                        ) : (
                          <span onClick={() => { setEditCell({ ns: tr.namespace, key: tr.key, lang }); setEditValue(val); }}
                            className={`cursor-pointer hover:bg-blue-50 rounded px-1 py-0.5 block truncate ${isMissing ? 'text-red-400 italic' : 'text-gray-700'}`}>
                            {val || '— brak —'}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add language modal */}
      <Modal isOpen={showAddLang} onClose={() => setShowAddLang(false)} title="Dodaj język">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kod (np. de, fr, es)</label>
            <input type="text" value={newLang.code} onChange={e => setNewLang({ ...newLang, code: e.target.value.toLowerCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" maxLength={5} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa (angielska)</label>
            <input type="text" value={newLang.name} onChange={e => setNewLang({ ...newLang, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="np. German" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa natywna</label>
            <input type="text" value={newLang.native_name} onChange={e => setNewLang({ ...newLang, native_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="np. Deutsch" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowAddLang(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
            <button onClick={handleAddLang} disabled={!newLang.code || !newLang.name || !newLang.native_name}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg">{t('common.add')}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
